import { AccommodationPlaceRoomBlockStatus, AccommodationsInfoFragment, useUpdateAccommodationPlaceV2Mutation } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAdminAccommodationsTelemetry } from '../Accommodations.telemetry';
import { formatDateForTravel } from '@shared/utils/dateHelpers';
import { formatNowForTelemetry } from '@shared/utils/telemetryDateFormatter';

interface CancelRoomBlockProps {
  pageSlug: string;
  eventHandle: string;
  isSuperAdmin: boolean;
  displayName: string;
  onCancelled: () => void;
  onCancelError: () => void;
  accommodationsData?: AccommodationsInfoFragment;
}

export function useCancelRoomBlock(props: CancelRoomBlockProps) {
  const { pageSlug, eventHandle, isSuperAdmin, displayName, onCancelled, onCancelError, accommodationsData } = props;
  const telemetry = useAdminAccommodationsTelemetry();

  const [updateAccommodationPlaceV2, { loading: isLoadingUpdateV2 }] = useUpdateAccommodationPlaceV2Mutation({ onCompleted: onCancelled, onError: onCancelError });

  const onCancelRoomBlock = useEventCallback(
    (id: string, cancellationReason: string) => {
      if (id) {
        const currentDateTime = formatNowForTelemetry();
        telemetry.cancelRoomBlockConfirm(pageSlug, {
          roomBlockId: id,
          cancellationReason,
          isSuperAdmin,
          website: eventHandle,
          datetime: currentDateTime,
          hotelName: displayName,
        });
        const roomBlockData = accommodationsData?.accommodationPlacesV2.find(i => i.id === id);
        updateAccommodationPlaceV2({
          variables: {
            payload: {
              id,
              accommodationRoomBlock: {
                status: AccommodationPlaceRoomBlockStatus.cancelled,
                bookingUrl: roomBlockData?.accommodationRoomBlock?.bookingUrl ?? '',
                checkInDate: roomBlockData?.accommodationRoomBlock?.checkInDate ?? formatDateForTravel(new Date()),
                checkOutDate: roomBlockData?.accommodationRoomBlock?.checkOutDate ?? formatDateForTravel(new Date()),
                cutoffDate: roomBlockData?.accommodationRoomBlock?.cutoffDate ?? formatDateForTravel(new Date()),
                numberOfRoomsReserved: roomBlockData?.accommodationRoomBlock?.numberOfRoomsReserved ?? 0,
                isJoyManagedRoomBlock: roomBlockData?.accommodationRoomBlock?.isJoyManagedRoomBlock ?? false,
                pricePerNight: roomBlockData?.accommodationRoomBlock?.pricePerNight ?? 0,
                strikeoutPricePerNight: roomBlockData?.accommodationRoomBlock?.strikeoutPricePerNight ?? 0,
              },
            },
          },
          refetchQueries: ['AccommodationHotels'],
        });
      }
    },
    [pageSlug, eventHandle, isSuperAdmin, displayName, telemetry, accommodationsData, updateAccommodationPlaceV2],
  );

  return { canceling: isLoadingUpdateV2, onCancelRoomBlock };
}
